import { type FieldPath, type FieldValues, type UseFormSetError } from "react-hook-form"
import type { ApiErrorIssues } from "@ibada/shared-types/envelopes"

import { ApiError } from "@/shared/lib/api/http"

type ApiFormIssues = Partial<ApiErrorIssues> & {
  formErrors?: unknown
  fieldErrors?: unknown
}

export type ApiFormErrorDetails = {
  code?: string
  message: string
  status: number | null
  formErrors: string[]
  fieldErrors: Record<string, string[]>
}

type ApplyApiFieldErrorsOptions<TFieldValues extends FieldValues> = {
  fieldNameMap?: Partial<Record<string, FieldPath<TFieldValues> | null>>
  /**
   * Transform each backend field message before it is set on the field. Used by
   * `useApplyApiFieldErrors` to translate codes/prose into localized copy. When omitted the raw
   * backend string is used (deprecated — prefer the translated hook).
   */
  transformMessage?: (message: string, fieldName: string) => string
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  return value
    .filter((entry): entry is string => typeof entry === "string")
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function normalizeFieldErrors(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {}
  }

  const normalized: Record<string, string[]> = {}

  for (const [fieldName, messages] of Object.entries(value as Record<string, unknown>)) {
    const rows = toStringArray(messages)
    if (rows.length === 0) continue
    normalized[fieldName] = rows
  }

  return normalized
}

function normalizeFieldKey(key: string) {
  return key.trim().toLowerCase().replace(/[\s-]+/g, "")
}

function resolveFieldPath<TFieldValues extends FieldValues>(
  rawFieldName: string,
  fieldNameMap?: Partial<Record<string, FieldPath<TFieldValues> | null>>,
): FieldPath<TFieldValues> | null {
  const directMapped = fieldNameMap?.[rawFieldName]
  if (typeof directMapped === "string") {
    return directMapped
  }

  const normalizedName = normalizeFieldKey(rawFieldName)
  for (const [candidate, mappedField] of Object.entries(fieldNameMap ?? {})) {
    if (normalizeFieldKey(candidate) === normalizedName && typeof mappedField === "string") {
      return mappedField
    }
  }

  return rawFieldName as FieldPath<TFieldValues>
}

export function getApiFormErrorDetails(error: unknown): ApiFormErrorDetails {
  const status = error instanceof ApiError ? error.status : null
  const code = error instanceof ApiError ? error.code : undefined
  const message = error instanceof Error ? error.message : "Request failed."
  const issues = error instanceof ApiError ? (error.issues as ApiFormIssues | undefined) : undefined

  return {
    code,
    message,
    status,
    formErrors: toStringArray(issues?.formErrors),
    fieldErrors: normalizeFieldErrors(issues?.fieldErrors),
  }
}

export function applyApiFieldErrors<TFieldValues extends FieldValues>(
  setError: UseFormSetError<TFieldValues>,
  error: unknown,
  options?: ApplyApiFieldErrorsOptions<TFieldValues>,
) {
  const { fieldErrors } = getApiFormErrorDetails(error)

  for (const [rawFieldName, messages] of Object.entries(fieldErrors)) {
    const fieldPath = resolveFieldPath(rawFieldName, options?.fieldNameMap)
    const firstMessage = messages[0]
    if (!fieldPath || !firstMessage) continue

    setError(fieldPath, {
      type: "server",
      message: options?.transformMessage ? options.transformMessage(firstMessage, rawFieldName) : firstMessage,
    })
  }
}

/**
 * @deprecated Returns the RAW backend message (untranslated). Use `useApiFormBannerMessage` (form
 * banners) or `useApiErrorToast` (toasts) from the shared error-translation layer instead.
 */
export function getApiFormErrorMessage(error: unknown, fallbackMessage: string) {
  const details = getApiFormErrorDetails(error)
  return details.formErrors[0] ?? details.message ?? fallbackMessage
}
